import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Presentation,
    Loader2,
    Download,
    RefreshCw,
    CheckCircle2,
    AlertTriangle,
    Clock,
    Layers,
    ArrowLeft,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API_BASE, extractError, tokenStore } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const STATE_META = {
    idle: { label: "Idle", tone: "slate", pill: "bg-slate-100 text-slate-700 border-slate-200" },
    generating: { label: "Generating", tone: "blue", pill: "bg-[#1E56A0]/10 text-[#1E56A0] border-[#8AC4FF]" },
    ready: { label: "Ready", tone: "emerald", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    failed: { label: "Failed", tone: "rose", pill: "bg-rose-50 text-rose-700 border-rose-200" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
}

function fmtDuration(s) {
    const secs = Math.max(0, Math.round(Number(s) || 0));
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    const r = secs % 60;
    return `${m}m ${r}s`;
}

function fmtSize(bytes) {
    if (!bytes) return "—";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

export default function DeckAdminPage() {
    const [status, setStatus] = useState(null);
    const [thumbs, setThumbs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);
    const pollRef = useRef(null);
    const token = tokenStore.get();

    const isGenerating = status?.state === "generating";

    const loadStatus = useCallback(async () => {
        try {
            const [{ data: latest }, { data: thumbData }] = await Promise.all([
                api.get("/admin/deck/latest"),
                api.get("/admin/deck/thumbnails"),
            ]);
            setStatus(latest);
            setThumbs(thumbData.items || []);
        } catch (e) {
            toast.error(extractError(e, "Could not load deck status"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    // Poll every 3s while generating.
    useEffect(() => {
        if (isGenerating) {
            pollRef.current = setInterval(loadStatus, 3000);
            return () => clearInterval(pollRef.current);
        }
        if (pollRef.current) clearInterval(pollRef.current);
    }, [isGenerating, loadStatus]);

    const meta = STATE_META[status?.state] || STATE_META.idle;

    const handleRegenerate = async () => {
        try {
            setRegenerating(true);
            await api.post("/admin/deck/regenerate");
            toast.success("Deck generation started — this typically takes 2–4 minutes.");
            await loadStatus();
        } catch (e) {
            toast.error(extractError(e, "Could not start deck generation"));
        } finally {
            setRegenerating(false);
        }
    };

    const handleDownload = async () => {
        try {
            const resp = await api.get("/admin/deck/download", { responseType: "blob" });
            const blob = new Blob([resp.data], {
                type:
                    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = status?.filename || "ESSS_Platform_Overview.pptx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(extractError(e, "Could not download deck"));
        }
    };

    const progressPct = useMemo(() => {
        const v = Number(status?.progress_pct || 0);
        return Math.max(0, Math.min(100, v));
    }, [status]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24 text-[#0B2545]/70" data-testid="deck-loading">
                <Loader2 className="animate-spin" size={20} />
                <span className="ml-2 text-sm">Loading deck status…</span>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="deck-admin-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <Link
                        to="/app/dashboard"
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.25em] text-[#1E56A0] hover:underline"
                        data-testid="deck-back-link"
                    >
                        <ArrowLeft size={12} /> Dashboard
                    </Link>
                    <h1
                        className="mt-2 text-3xl sm:text-4xl font-serif text-[#0B2545]"
                        style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                        data-testid="deck-page-title"
                    >
                        Client Presentation Deck
                    </h1>
                    <p className="mt-1 text-sm text-[#0E1B2C]/70 max-w-2xl">
                        Auto-generated PowerPoint tour of the platform. Screenshots are captured
                        against the live preview, then assembled into a 32-slide 16:9 deck.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        onClick={handleRegenerate}
                        disabled={isGenerating || regenerating}
                        className="bg-[#0B2545] hover:bg-[#0E1B2C] text-white gap-2"
                        data-testid="deck-regenerate-btn"
                    >
                        {isGenerating || regenerating ? (
                            <Loader2 className="animate-spin" size={16} />
                        ) : (
                            <RefreshCw size={16} />
                        )}
                        {isGenerating ? "Generating…" : "Regenerate deck"}
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={!status?.has_deck}
                        variant="outline"
                        className="border-[#1E56A0] text-[#1E56A0] hover:bg-[#8AC4FF]/20 gap-2"
                        data-testid="deck-download-btn"
                    >
                        <Download size={16} /> Download .pptx
                    </Button>
                </div>
            </div>

            {/* Status card */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-[#D6E6F2] bg-white overflow-hidden"
                data-testid="deck-status-card"
            >
                <div
                    className="px-6 py-5 flex flex-wrap items-center gap-3"
                    style={{ background: "linear-gradient(90deg, #F1F6FB 0%, #FFFFFF 100%)" }}
                >
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#0B2545] text-white">
                        <Presentation size={20} />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#1E56A0]">
                            Current state
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge
                                variant="outline"
                                className={`${meta.pill} border font-semibold`}
                                data-testid="deck-state-badge"
                            >
                                {meta.label}
                            </Badge>
                            {status?.state === "ready" && (
                                <span className="text-xs text-[#0E1B2C]/60 inline-flex items-center gap-1">
                                    <CheckCircle2 size={12} className="text-emerald-600" />
                                    Last generated {fmtDate(status.generated_at)}
                                </span>
                            )}
                            {status?.state === "failed" && (
                                <span className="text-xs text-rose-700 inline-flex items-center gap-1">
                                    <AlertTriangle size={12} /> Last run failed
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="ml-auto text-right text-xs text-[#0E1B2C]/60">
                        <div className="flex items-center gap-1 justify-end">
                            <Layers size={12} /> Slides: {status?.slide_count || "—"}
                        </div>
                        <div className="flex items-center gap-1 justify-end mt-0.5">
                            <Clock size={12} /> Size: {fmtSize(status?.size_bytes)}
                        </div>
                    </div>
                </div>

                {/* Progress */}
                <div className="px-6 py-5 border-t border-[#D6E6F2]/70 space-y-4">
                    <div>
                        <div className="flex items-center justify-between text-xs text-[#0E1B2C]/70">
                            <span data-testid="deck-progress-step">
                                {status?.current_step || (status?.state === "ready" ? "Ready to download" : status?.state === "failed" ? "Awaiting retry" : "Idle")}
                            </span>
                            <span className="font-semibold text-[#0B2545]" data-testid="deck-progress-pct">
                                {progressPct}%
                            </span>
                        </div>
                        <div className="mt-2 h-2 w-full rounded-full bg-[#D6E6F2] overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                    status?.state === "failed"
                                        ? "bg-rose-500"
                                        : status?.state === "ready"
                                        ? "bg-emerald-500"
                                        : "bg-gradient-to-r from-[#1E56A0] to-[#8AC4FF]"
                                }`}
                                style={{ width: `${progressPct}%` }}
                                data-testid="deck-progress-bar"
                            />
                        </div>
                    </div>

                    {status?.state === "failed" && status?.error_message && (
                        <div
                            className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
                            data-testid="deck-error-message"
                        >
                            <div className="font-semibold flex items-center gap-2">
                                <AlertTriangle size={14} /> Last run failed
                            </div>
                            <div className="mt-1 font-mono text-xs break-words">{status.error_message}</div>
                            {status.has_deck && (
                                <div className="mt-2 text-xs text-rose-900/80">
                                    The previous successful deck remains downloadable.
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-[#0E1B2C]/70">
                        <MetaBox label="Started" value={fmtDate(status?.started_at)} />
                        <MetaBox label="Completed" value={fmtDate(status?.completed_at)} />
                        <MetaBox label="Duration" value={status?.duration_s ? fmtDuration(status.duration_s) : "—"} />
                        <MetaBox
                            label="Version"
                            value={status?.generated_at ? new Date(status.generated_at).toISOString().slice(0, 19) + "Z" : "—"}
                            mono
                        />
                    </div>
                </div>
            </motion.div>

            {/* Thumbnails */}
            <section data-testid="deck-thumbnails-section">
                <div className="flex items-baseline justify-between mb-3">
                    <h2
                        className="text-lg font-serif text-[#0B2545]"
                        style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                    >
                        First 6 slides
                    </h2>
                    <div className="text-xs text-[#0E1B2C]/50">
                        {thumbs.length ? `${thumbs.length} preview thumbnail${thumbs.length === 1 ? "" : "s"}` : "No preview yet"}
                    </div>
                </div>

                {thumbs.length === 0 ? (
                    <div
                        className="rounded-2xl border border-dashed border-[#D6E6F2] bg-white p-10 text-center text-sm text-[#0E1B2C]/60"
                        data-testid="deck-thumbs-empty"
                    >
                        <Presentation className="mx-auto mb-3 text-[#1E56A0]" />
                        No thumbnails available yet. Click <strong>Regenerate deck</strong> to
                        capture screenshots and assemble the deck.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deck-thumbs-grid">
                        {thumbs.map((t) => (
                            <ThumbnailCard key={t.index} thumb={t} token={token} />
                        ))}
                    </div>
                )}
            </section>

            {/* Docs footer */}
            <div className="rounded-2xl bg-[#F1F6FB] border border-[#D6E6F2] p-5 text-xs text-[#0E1B2C]/70 space-y-1">
                <div className="font-semibold text-[#0B2545] text-sm">About this deck</div>
                <div>
                    The generator logs in as five seeded users (admin, org leader, reviewer, coach, teacher)
                    and takes 32 screenshots spanning the public marketing surface, auth flow, admin backend,
                    assessment workflow, executive reporting, and the Leadership Reflection Framework.
                </div>
                <div>
                    Screenshots are captured at 1440×900 (2× DPI) after network idle + Framer Motion settle.
                    The final <span className="font-mono">.pptx</span> is uploaded to Emergent Object Storage;
                    the previous version remains downloadable if a new run fails midway.
                </div>
            </div>
        </div>
    );
}

function MetaBox({ label, value, mono }) {
    return (
        <div className="rounded-lg border border-[#D6E6F2] bg-[#F8FBFF] px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0]/80">{label}</div>
            <div className={`mt-0.5 text-sm text-[#0B2545] ${mono ? "font-mono text-xs" : ""}`}>
                {value}
            </div>
        </div>
    );
}

function ThumbnailCard({ thumb, token }) {
    // Because the thumbnail endpoint is Bearer-protected, we cannot set it as
    // a plain <img src>. Fetch it as a blob and render an object URL instead.
    const [src, setSrc] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        (async () => {
            try {
                const resp = await api.get(`/admin/deck/thumbnails/${thumb.index}.jpg`, {
                    responseType: "blob",
                });
                if (cancelled) return;
                objectUrl = URL.createObjectURL(resp.data);
                setSrc(objectUrl);
            } catch (e) {
                if (!cancelled) setError(true);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [thumb.index, token]);

    return (
        <div
            className="rounded-2xl border border-[#D6E6F2] bg-white overflow-hidden group hover:border-[#1E56A0] transition-colors"
            data-testid={`deck-thumb-${thumb.index}`}
        >
            <div className="aspect-video bg-[#F1F6FB] relative overflow-hidden">
                {src ? (
                    <img
                        src={src}
                        alt={`Slide ${thumb.index}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                ) : error ? (
                    <div className="w-full h-full flex items-center justify-center text-xs text-[#0E1B2C]/50">
                        Failed to load
                    </div>
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#1E56A0]/60">
                        <Loader2 className="animate-spin" size={16} />
                    </div>
                )}
            </div>
            <div className="px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-mono text-[#0B2545]/70">
                    Slide {String(thumb.index).padStart(2, "0")}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-[#1E56A0]">
                    Preview
                </span>
            </div>
        </div>
    );
}
